"use client"

import { useState } from "react"

import { useTranslations } from "next-intl"

import { Button, ChevronIcon, Pill, Stack } from "@/components/ui"
import type { FacetValue } from "@/lib/types/search"

interface PillFacetProps {
  options: FacetValue[]
  selected: string[]
  onToggle: (value: string) => void
}

const COLLAPSED_COUNT = 16

export const PillFacet = ({ options, selected, onToggle }: PillFacetProps) => {
  const t = useTranslations("search")
  const [showAll, setShowAll] = useState(false)

  const popular = new Set(
    [...options]
      .sort((a, b) => b.count - a.count)
      .slice(0, COLLAPSED_COUNT)
      .map((option) => option.value)
  )
  const forceExpand = options.some((option) => selected.includes(option.value) && !popular.has(option.value))
  const collapsed = !showAll && !forceExpand && options.length > COLLAPSED_COUNT
  const visible = collapsed ? options.filter((option) => popular.has(option.value)) : options

  return (
    <Stack gap="250">
      <div className="flex flex-wrap gap-150">
        {visible.map((option) => (
          <Pill
            key={option.value}
            tone="outline"
            size="xs"
            selected={selected.includes(option.value)}
            className="min-w-11 tabular-nums"
            aria-label={t("facetOption", { label: option.label, count: option.count })}
            onClick={() => onToggle(option.value)}
          >
            {option.label}
          </Pill>
        ))}
      </div>

      {!forceExpand && options.length > COLLAPSED_COUNT && (
        <Button variant="quiet" onClick={() => setShowAll((value) => !value)} className="text-fg-brand font-text text-copy -mx-200 justify-start gap-100 px-200 py-100 font-normal">
          <ChevronIcon size={12} className={`transition-transform ${showAll ? "-rotate-90" : "rotate-90"}`} />
          {showAll ? t("brandFacet.showLess") : t("brandFacet.showMore", { count: options.length - COLLAPSED_COUNT })}
        </Button>
      )}
    </Stack>
  )
}
